import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { Issue } from "generated-graphql/graphql";
import { isEqual } from "lodash";
import { Component, Ref } from "react";
import { DocumentRecommendationRow } from "routes/Customer/Documents/DocumentsGrid";
import { DataGridCell } from "./DataGridCell";
import { LinkContent } from "./LinkContent";

export type CustomGridColDef<
  Row extends GridValidRowModel = Record<string, any>
> = GridColDef<Row> & {
  topAligned?: boolean;
  /**
   * Whether to display cell content wrapped in a link to the getRowLink href.
   * Behavior is automatic if getRowLink is provided. Set this to false if you want a column to explicitly NOT render as a link.
   */
  linkable?: boolean;
};

export type DataGridProps<Row extends GridValidRowModel> = React.ComponentProps<
  typeof DataGrid<Row>
>;

// Custom loading overlay component for DataGrid
const DataGridLoadingOverlay = () => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="absolute"
      top="var(--DataGrid-headersTotalHeight)"
      left={0}
      right={0}
      bottom="var(--DataGrid-hasScrollX, 0px)"
      bgcolor={
        theme.palette.mode === "dark"
          ? "rgba(0, 0, 0, 0.5)"
          : "rgba(255, 255, 255, 0.5)"
      }
      zIndex={1000}
    >
      <CircularProgress />
    </Box>
  );
};

// Light wrapper of MUI's DataGridPremium component where we can specify global styles that should apply to all tables
// any default props can and should be overridable.
export class DataGrid<Row extends GridValidRowModel> extends Component<
  Omit<DataGridPremiumProps<Row>, "columns"> & {
    innerRef?: Ref<HTMLDivElement | null>;
    noRowsMessage?: string;
    columns: CustomGridColDef<Row>[];
    getRowLink?: (row: Row) => string | null;
  }
> {
  private memoizedColumns: CustomGridColDef<Row>[] = [];
  private prevColumnsInput: CustomGridColDef<Row>[] = [];
  private memoizedRowCount = 0;
  private previousRowCount = 0;

  render() {
    const {
      innerRef,
      noRowsMessage,
      columns: columnsInput,
      getRowLink,
      onPaginationModelChange,
      rowCount,
      ...props
    } = this.props;

    const currentRowCount = rowCount ?? props.rows?.length ?? 0;
    if (currentRowCount > 0 || this.previousRowCount === 0) {
      this.memoizedRowCount = currentRowCount;
      this.previousRowCount = currentRowCount;
    }
    const rowMinimumHeight = 38;

    const hasTopAlignedColumn = columnsInput.some((col) => col.topAligned);

    if (!isEqual(this.prevColumnsInput, columnsInput)) {
      this.memoizedColumns = columnsInput.map((column) => {
        const originalRender = column.renderCell;
        const isActionsColumn =
          column.field === "actions" || column.type === "actions";
        const explicitlyNonLinkable = column.linkable === false;
        const shouldLink =
          !isActionsColumn && !explicitlyNonLinkable && !!getRowLink;
        const shouldTopAlign = column.topAligned;

        if (!shouldLink && !shouldTopAlign) {
          return column;
        }

        return {
          ...column,
          renderCell: (params) => {
            const row = params.row;
            const url = shouldLink ? getRowLink(row) : null;

            const value =
              originalRender?.(params) ??
              params.formattedValue ??
              row[column.field as keyof typeof row];

            let cellContent = value;

            if (url) {
              cellContent = (
                <LinkContent url={url} minHeight={rowMinimumHeight}>
                  {cellContent}
                </LinkContent>
              );
            }

            if (shouldTopAlign) {
              return <DataGridCell>{cellContent}</DataGridCell>;
            }

            return cellContent;
          },
        };
      });
      this.prevColumnsInput = columnsInput;
    }

    const columns = this.memoizedColumns;

    const pageSizeOptions = props.pageSizeOptions ?? [
      10, 25, 50, 100, 200, 300, 400, 500,
    ];
    // Extract numeric values since pageSizeOptions can be numbers or {value, label} objects
    const pageSizes = pageSizeOptions.map((option) =>
      typeof option === "number" ? option : option.value
    );
    const smallestOptionPageSize =
      pageSizes.length > 0 ? Math.min(...pageSizes) : 10;

    // In controlled mode, also consider the current pageSize in case it's not in pageSizeOptions
    const controlledPageSize = props.paginationModel?.pageSize;
    const smallestPageSize = controlledPageSize
      ? Math.min(smallestOptionPageSize, controlledPageSize)
      : smallestOptionPageSize;

    const defaultHideFooter = this.memoizedRowCount <= smallestPageSize;

    return (
      <DataGridPremium
        ref={innerRef}
        // Autosizer on mount if autosizeOptions are provided
        autosizeOnMount={!!this.props.autosizeOptions}
        rowHeight={38}
        disableColumnFilter={true}
        disablePivoting={true}
        disableRowGrouping={true}
        disableAggregation={true}
        onPaginationModelChange={onPaginationModelChange}
        slots={{
          loadingOverlay: DataGridLoadingOverlay,
          ...(noRowsMessage
            ? {
                NoResultsOverlay: () => (
                  <div style={{ padding: "2rem", textAlign: "center" }}>
                    {noRowsMessage}
                  </div>
                ),
              }
            : undefined),
          ...props.slots,
        }}
        slotProps={{
          ...props.slotProps,
        }}
        rowCount={
          props.paginationMode !== "server" ? undefined : this.memoizedRowCount
        }
        {...props}
        // Only set checkboxSelectionVisibleOnly when checkbox selection is actually enabled
        // This prevents MUI warnings when pagination is disabled but checkbox selection isn't used
        checkboxSelectionVisibleOnly={
          props.checkboxSelection
            ? props.checkboxSelectionVisibleOnly ?? true
            : undefined
        }
        hideFooterPagination={props.hideFooterPagination ?? defaultHideFooter}
        hideFooter={props.hideFooter ?? defaultHideFooter}
        columnHeaderHeight={props.columnHeaderHeight ?? 56} // this is the default; don't ask me
        pageSizeOptions={pageSizeOptions}
        columns={columns}
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            minHeight: "40px",
          },
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-pinnedColumnHeaders": {
            boxShadow: "none",
          },
          "& .MuiDataGrid-pinnedColumns": {
            boxShadow: "none",
          },
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none",
            },
          "& .MuiDataGrid-cell": {
            minHeight: `${rowMinimumHeight}px`,
            display: "flex",
            alignItems: "center",
          },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell:not(.actions)":
            {
              py: hasTopAlignedColumn ? "10px" : "inherit",
            },
          "& .wrapHeader": {
            lineHeight: "1rem",
          },
          "& .wrapHeader .MuiDataGrid-columnHeaderTitleContainer": {
            whiteSpace: "normal",
          },
          "& .wrapHeader .MuiDataGrid-colCellTitle, .wrapHeader .MuiDataGrid-columnHeaderTitle":
            {
              whiteSpace: "normal",
            },
          // Style for action cells
          "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']":
            {
              opacity: 0,
              transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          // Show actions when hovering anywhere on the row
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='actions'] div[role='menu']":
            {
              opacity: 1,
            },
          ...(props.sx ?? {}),
        }}
      />
    );
  }
}

/**
 * Component to render a cell with a primary and secondary (smaller) text value
 */
export const CellWithSecondaryText = ({
  primaryValue,
  secondaryValue,
}: {
  primaryValue: string | null | undefined;
  secondaryValue: string | null | undefined;
}) => {
  const theme = useTheme();
  return (
    <Stack direction="column" width="100%" sx={{ py: theme.spacing(1) }}>
      <Typography variant="body2" noWrap>
        {primaryValue}
      </Typography>
      <Typography variant="caption" noWrap sx={{ fontSize: "10px" }}>
        {secondaryValue}
      </Typography>
    </Stack>
  );
};

export const IssueMessageCell = ({ issue }: { issue: Issue }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        color: theme.palette.error.main,
        whiteSpace: "normal",
        wordWrap: "break-word",
      }}
      variant="body2"
    >
      {issue.message}
    </Typography>
  );
};

export const RecommendationMessageCell = ({
  recommendation,
}: {
  recommendation: DocumentRecommendationRow;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      spacing={theme.spacing(4)}
    >
      <Typography
        sx={{
          whiteSpace: "normal",
          wordWrap: "break-word",
        }}
        variant="body2"
      >
        {recommendation.message}
      </Typography>
      <Typography variant="caption">Recommended</Typography>
    </Stack>
  );
};
